import React from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import { useAlerts } from "../../context";
import { RCL as useTranslation } from "../../components/RCL";
import { ImageConnector } from "../../connectors/ImageConnector";
import { cloudinaryGenURL } from "../../utils/cloudinaryHelpers";
import { aspectRatioMapping } from "../../utils/Images/getAspectRatioMapping";
import { GalleryConnector, galleryVariants } from "../../connectors/GalleryConnector";
import { BaseLayout } from "../../components/Layouts/BaseLayout";
import {
	Section,
	SectionTitleAsElement,
	SectionTitleSize,
	SectionPadding,
} from "../../components/Section";
import { DescriptionTabs } from "../../connectors/DescriptionTabsConnector/DescriptionTabs";
import { Cell, Row } from "../../components/Grid";
import { Heading, HeadingSize, HeadingAsElement } from "@sunwing/wvi-components";
import { FluidSections } from "../../connectors/FluidSections";
import { DestinationTilesConnector } from "../../connectors/DestinationTilesConnector";
import { ThingsToDoConnector } from "../../connectors/ThingsToDoConnector";
import { useBreakpoints } from "../../hooks";
import { CityTilesConnector } from "../../connectors/CityTilesConnector";
import * as styles from "./destination.module.scss";

const Destination = ({ data, pageContext, location }) => {
	const pageData = data.contentfulDestination;
	const { alertsCount } = useAlerts();

	const translations = {
		thingsToDo: useTranslation({ searchKey: "top-things-to-do" }),
	};
	const { isSm, isXs } = useBreakpoints();

	function hasMerchandisingData() {
		return (
			pageData?.sections?.some(section =>
				section?.components?.some(component => component?.merchandising)
			) || false
		);
	}

	return (
		<BaseLayout
			location={location}
			pageType={pageContext?.pageType}
			pageContext={pageContext}
			seo={pageData.seo}
			altLangPages={data.altLangPages}
			alerts={alertsCount}>
			<Section>
				<Section padding={SectionPadding.small}>
					<Heading Tag={HeadingAsElement.h1} size={HeadingSize.xl} className={styles.headingMargin}>
						{pageData.displayTitle}
					</Heading>
					{pageData.imageCarousel && (
						<GalleryConnector
							variant={
								pageData.imageCarousel.images.length >= 5
									? galleryVariants.mosaic
									: galleryVariants.default
							}
							data={{
								images: pageData?.imageCarousel?.images ?? [],
								aspectRatio: isSm || isXs ? aspectRatioMapping["1:1"] : aspectRatioMapping["16:5"],
							}}
							usedInFullWidthContainer={true}
						/>
					)}
					{(pageData.overviewDescription ||
						pageData.thingsToDoDescription ||
						pageData.travelTipsDescription) && (
						<>
							<DescriptionTabs
								overviewTab={pageData.overviewDescription}
								travelTipsTab={pageData.travelTipsDescription}
								hasMerchandisingData={hasMerchandisingData(pageData.sections)}
							/>
						</>
					)}
				</Section>

				{pageData.subDestinations && pageData.subDestinations?.length > 0 && (
					<Section
						title={pageData.hotelsOrDestinationsTitle}
						titleAs={SectionTitleAsElement.h2}
						titleSize={SectionTitleSize.lg}>
						<CityTilesConnector cities={pageData.subDestinations} basePath={pageContext.basePath} />
					</Section>
				)}

				{!!data.topHotels.edges.length && (
					<Row>
						<Cell>
							<Heading Tag={HeadingAsElement.h2} size={HeadingSize.lg} className={styles.topHotels}>
								{pageData.hotelsOrDestinationsTitle}
							</Heading>
							<DestinationTilesConnector
								hotels={data.topHotels.edges}
								locale={pageContext.locale}
							/>
						</Cell>
					</Row>
				)}
			</Section>
			{data?.contentfulDestination?.destinationCode && (
				<Section
					title={
						data?.contentfulDestination?.excursionTitleOverwrite ||
						translations.thingsToDo?.replace("{{DESTINATION}}", pageData?.destinationName)
					}
					titleAs={SectionTitleAsElement.h2}
					titleSize={SectionTitleSize.lg}
					className={styles.thingsToDoSection}
					titleId="things-to-do">
					<ThingsToDoConnector destinationCode={data?.contentfulDestination?.destinationCode} />
				</Section>
			)}
			{!!pageData.sections?.length && <FluidSections data={pageData.sections} />}

			{pageData.logo && (
				<Section theme="light">
					<div className={styles.logo}>
						<ImageConnector
							alt={pageData.displayTitle}
							image={cloudinaryGenURL({
								cloudinaryImage: pageData.logo?.[0],
							})}
						/>
					</div>
				</Section>
			)}
		</BaseLayout>
	);
};

Destination.propTypes = {
	data: PropTypes.object.isRequired,
	pageContext: PropTypes.object.isRequired,
	location: PropTypes.object.isRequired,
};
export default Destination;

export const pageQuery = graphql`
	query GetDestinationPageData(
		$id: String!
		$ctf_id: String!
		$destination: String!
		$destinationRegionCode: String
		$lang: String!
	) {
		contentfulDestination(id: { eq: $id }) {
			displayTitle
			hotelsOrDestinationsTitle
			slug
			destinationCode
			destinationName
			excursionTitleOverwrite
			sections {
				__typename
				...AlertBanner
				...Collection
				...ComponentBlock
				...Gallery
				...HardCodedComponent
				...LinkButtonWithModal
				...ListLink
				...MultiImageBlock
				...Paragraph
				...PromotionRule
				...ResponsiveBanner
				...Section
				...SplitContentWrapper
				...TabsAndAccordion
				...TimedComponent
				...TopTile
				...VideoPlayer
			}
			seo {
				...SEO
			}
			imageCarousel {
				...Gallery
			}
			logo {
				url
				secure_url
				width
				height
				raw_transformation
				version
				public_id
				format
			}
			overviewDescription {
				...Paragraph
			}
			travelTipsDescription {
				...Paragraph
			}
			subDestinations {
				destinationName
				slug
				imageCarousel {
					...Gallery
				}
			}
		}
		altLangPages: allSitePage(
			filter: { context: { ctf_id: { eq: $ctf_id }, destination: { eq: $destination } } }
		) {
			edges {
				...LanguageToggle
			}
		}
		topHotels: allHotel(
			filter: {
				data: { LanguageCode: { eq: $lang }, DestinationRegionCode: { eq: $destinationRegionCode } }
			}
			sort: [{ data: { StarRating: DESC } }, { data: { HotelName: ASC } }]
		) {
			edges {
				node {
					data {
						LanguageCode
						HotelName
						HotelCode
						RegionName
						DestinationName
						StarRating
						MealPlanFeatures {
							MealPlan {
								Name
								Description
							}
						}
						HotelImages {
							images {
								src
							}
						}
					}
				}
			}
		}
	}
`;
